import { useJSONFetcher } from "@outplayed/json-fetcher";
import SubtitleAndDescription from "components/Common/SubtitleAndDescription";
import React from "react";
import { useResponsive } from "@outplayed/responsive";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import { useQueryString } from "../../../hooks/general-hooks";
import { getEncounterId, useWowPath } from "../../../hooks/path-hooks";
import {
  EXPANSION,
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getDifficultyPath,
  getFormattedContentType,
  getFormattedEncounterType,
  getFormattedNames,
  getIcyVeinsLinkName,
  getIcyVeinsRole,
  PATCH,
} from "../../../lib/wowHelpers";
import NoData from "../../Common/NoData";
import PvEGearGuide from "../../GearGuide/PvEGearGuide";
import TierSetContainer from "../Builds/TierSetContainer";
import Layout from "../Layout";
import GearContainer, { getPrimaryMetric, ItemsSubContainerCategory } from "./GearContainer";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import { ReactComponent as AlertIcon } from "svg/alert-fill.svg";
import { ReactComponent as ExternalLinkIcon } from "svg/external-link-line.svg";
import { ReactComponent as IcyVeinsLogo } from "svg/icy_veins_logo.svg";
import { ReactComponent as ChevronIcon } from "svg/chevron-back.svg";
import { MediaQuery } from "@outplayed/responsive";
import { SPECS_TO_HERO_TREES } from "lib/classes-and-specs";
import { TablePageSkeleton } from "components/Common/PageSkeletons";

const GearPageContainer = () => {
  const { currentBreakpoint } = useResponsive();
  const isMobile =
    currentBreakpoint === "MOBILE_SMALL" || currentBreakpoint === "MOBILE_MEDIUM" || currentBreakpoint === "MOBILE_LARGE";

  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const encounterID = getEncounterId(optionalParams, encounterType);
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);

  const { difficulty, affix } = useQueryString();
  const diffPath = getDifficultyPath(difficulty, encounterType);
  const { data: gearTable, loading: isLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${diffPath}/${dataClassName}/${dataClassName}_itemsTable.json`,
    { ssr: true },
  );

  const pageDescription = `Data-driven ${displaySpecName} ${displayClassName} recommended gear guide from top players. Follow these ${
    SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]
  } and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} gear recommendations on your journey to BIS for ${getFormattedEncounterType(
    encounterType,
  )} in ${EXPANSION} ${PATCH}.`;

  const metaDescription = `Data-driven ${displaySpecName} ${displayClassName} gear from top players. The best ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} gear recommendations in each slot for ${getFormattedEncounterType(
    encounterType,
  )} in ${EXPANSION} ${PATCH}.`;

  const layoutProps = {
    className: "wow-gear-page-container",
    classType,
    filterPage: "builds",
    metaTitle: `${displaySpecName} ${displayClassName} Gear for ${getFormattedEncounterType(encounterType)}`,
    metaDescription,
    pageDescription,
    pageTitle: `Gear for ${getFormattedEncounterType(encounterType)}`,
    specType,
  };

  if (isLoading) {
    return (
      <Layout {...layoutProps}>
        <TablePageSkeleton />
      </Layout>
    );
  }

  const dataClassSpec = `${dataClassName}-${dataSpecName}`;
  const hasData =
    gearTable &&
    (affix && affix !== "all"
      ? (gearTable.affixes?.[affix]?.[encounterID]?.[dataClassSpec]?.items_table?.items?.belt?.total_count ?? 0) > 0
      : (gearTable[encounterType]?.[encounterID]?.[dataClassSpec]?.items_table?.items?.belt?.total_count ?? 0) > 0);

  const classSpecGear =
    hasData &&
    (affix && affix !== "all"
      ? gearTable.affixes[affix][encounterID][dataClassSpec].items_table
      : gearTable[encounterType][encounterID][dataClassSpec].items_table);
  const bisGear =
    hasData &&
    (affix && affix !== "all"
      ? gearTable.affixes[affix][encounterID][dataClassSpec]
      : gearTable[encounterType][encounterID][dataClassSpec]);

  const specClass = `${displaySpecName} ${displayClassName}`;
  const specClass2 = `${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(displayClassName)}`;
  const contentType = getFormattedContentType(encounterType);
  const ivGearGuideURL = `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
    classType,
  )}-pve-${getIcyVeinsRole(specType)}-gear-best-in-slot`;

  const EmbellishmentsAlert = () => (
    <div className="embellishments-alert">
      <div className="left-side">
        <AlertIcon className="alert-icon" />{" "}
        <div className="text-container">
          Due to technical limitations, we can't provide data for all embellishments. Check out{" "}
          <MediaQuery min="MOBILE_SMALL" max="TABLET" renderNullOnFail>
            <div>
              <strong>Icy Veins</strong>
            </div>
          </MediaQuery>
          <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail>
            <div>
              <a href={ivGearGuideURL} target="_blank">
                <strong>Icy Veins</strong>
              </a>
            </div>
          </MediaQuery>{" "}
          for the best choices!
        </div>
      </div>
      <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail>
        <a href={ivGearGuideURL} target="_blank">
          <div className="right-side">
            <IcyVeinsLogo className="icy-veins-logo" />
            <ChevronIcon className="chevron-icon" />
          </div>
        </a>
      </MediaQuery>
    </div>
  );

  return (
    <Layout {...layoutProps}>
      {hasData ? (
        <>
          <PvEGearGuide
            builds={bisGear.items}
            combos={bisGear.combos}
            classType={classType}
            specType={specType}
            encounterType={encounterType}
            encounter={encounterID}
          />
          {/* <TierSetContainer className={displayClassName} specName={displaySpecName} heroSpecName={dataHeroName} encounterType={encounterType} /> */}
          <div className="wow-items-page embellishments-container">
            <MediaQuery min="MOBILE_SMALL" max="TABLET" renderNullOnFail>
              <a href={ivGearGuideURL} target="_blank">
                <EmbellishmentsAlert />
              </a>
            </MediaQuery>
            <MediaQuery min="DESKTOP_SMALL" max="DESKTOP_LARGE" renderNullOnFail>
              <EmbellishmentsAlert />
            </MediaQuery>
            <ItemsSubContainerCategory
              category="Embellishment Items"
              metricOneName={getPrimaryMetric(displaySpecName, encounterType)}
              metricTwoName="Popularity"
              data={classSpecGear.embellishments}
              count={classSpecGear.embellishments.reduce((acc, x) => acc + x.count, 0)}
            />
          </div>
          <div className="seperator" />
          <SubtitleAndDescription
            subtitle={`${specClass2} Gear Tables`}
            description={`These tables are a comparison of the most popular items in each gear slot for ${contentType}.`}
          />
          <GearContainer
            classSpecGear={classSpecGear}
            isMobile={isMobile}
            displaySpecName={displaySpecName}
            encounterType={encounterType}
          />
          <PageSectionCrossLinks
            classType={classType}
            description={`Our friends at Icy Veins have you covered, with an in-depth guide to suit up your ${displaySpecName} ${displayClassName} with the best gear.`}
            guideData={[
              {
                description: `Find the best in slot gear for ${displaySpecName} ${displayClassName}`,
                link: ivGearGuideURL,
                title: "Gear and Best in Slot Guide",
              },
            ]}
            specType={specType}
          />
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default GearPageContainer;
